import React, { useCallback, useEffect, useRef, useState } from 'react';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { useLocalStorage, useMediaQuery } from 'usehooks-ts';
import { Attachment } from 'graphql-common';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ImageViewer from '@lib/components/ImageViewer/ImageViewer';
import Modal from '@lib/components/Modal/Modal';
import PDFViewer from '@lib/components/PDFViewer/PDFViewer';
import VideoViewer from '@lib/components/VideoViewer/VideoViewer';
import {
  VIDEO_PROCESSING_POLL_INTERVAL_MS,
  VIDEO_PROCESSING_POLL_TIMEOUT_MS,
} from '@lib/components/VideoViewer/constants';
import { ModalMode } from '@lib/components/Modal/enums';
import { FILE_PREVIEW_MODAL_MODE } from '@lib/enums/localStorageKeys';
import isMobile from '@lib/utils/isMobile';
import useDocumentationViewTracking from '@lib/utils/documentation/useDocumentationViewTracking';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { ErrorLike } from '@apollo/client';
import { BELOW_MD1_QUERY } from '@lib/assets/styles/breakpoints';
import videoStyles from '@lib/components/VideoViewer/VideoViewer.module.scss';
import useProcessingPolling from './useProcessingPolling';
import styles from './FileViewerModal.module.scss';

type Props = {
  attachment: Attachment | null | undefined;
  attachmentName?: string | null;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Enables document view tracking for this viewer. Pass the id of the
   * `DocumentationVersion` being shown; omit it for attachments that are not
   * documentation (intervention files, avatars, …). Safe to pass alongside
   * `useMarkAsReadTool` — `onMarkAsRead` settles into the same tracked view
   * record rather than creating a second one. See
   * `useDocumentationViewTracking`.
   */
  documentationVersionId?: string | null;
  swiperProps?: {
    onPrevious?: () => void;
    onNext?: () => void;
    isPreviousDisabled?: boolean;
    isNextDisabled?: boolean;
  };
  useDownloadTool?: boolean;
  useFavoriteTool?: boolean;
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
  useMarkAsReadTool?: boolean;
  /**
   * Whether the open document is already known to be read/watched by the
   * current user (e.g. `Documentation.read`, or an in-session confirmation
   * the caller tracked itself). Hides the "Mark as read" CTA instead of
   * prompting to reconfirm something already on record. Ignored unless
   * `useMarkAsReadTool` is also set.
   */
  isMarkedAsRead?: boolean;
  /**
   * Called after the user explicitly marks the document as read/watched AND
   * both the underlying `DocumentationVersionView` record and the
   * `documentationVersionMarkAsRead` confirmation have succeeded — never
   * optimistically. Receives the view record's id (for the caller to keep as
   * proof-of-read, e.g. a task submission payload) and the total
   * reading-duration seconds credited to it. Requires `documentationVersionId`
   * to be set.
   */
  onMarkAsRead?: (
    readingDurationSeconds: number,
    versionViewId: string,
  ) => void;
  /**
   * Optional callback fired periodically while a video is transcoding so the
   * parent can refetch the attachment payload. When provided, polling runs at
   * `VIDEO_PROCESSING_POLL_INTERVAL_MS` and caps at
   * `VIDEO_PROCESSING_POLL_TIMEOUT_MS`. When omitted, the placeholder still
   * renders but no polling is triggered.
   */
  onRefetchAttachment?: () => void;
};

export default function FileViewerModal(props: Props) {
  const { t } = useTranslation();
  const [modalMode, setModalMode] = useLocalStorage(
    FILE_PREVIEW_MODAL_MODE,
    ModalMode.Default,
  );
  const {
    attachment,
    isOpen,
    onClose,
    attachmentName,
    documentationVersionId,
    swiperProps,
    useDownloadTool = true,
    useFavoriteTool = false,
    onFavoriteToggle,
    isFavorited,
    isFavoriteLoading,
    useMarkAsReadTool = false,
    isMarkedAsRead = false,
    onMarkAsRead,
    onRefetchAttachment,
  } = props;

  const downloadUrl = attachment?.urls?.originalDirectDownload || '';
  const fileUrl = attachment?.urls?.large || attachment?.urls?.original || '';
  const mimeType = _get(attachment, [
    'fileData',
    'data',
    'metadata',
    'mimeType',
  ]) as string | undefined;
  const isPdf = mimeType?.includes('pdf');
  const isImage = mimeType?.includes('image');
  const isVideo = mimeType?.startsWith('video');

  // On mobile devices and below the $md1 breakpoint the modal is already
  // edge-to-edge, so we force fullscreen and hide the toggle (passing it as
  // `undefined` below) to keep the two states coherent.
  const isBelowMd1 = useMediaQuery(BELOW_MD1_QUERY);
  const { phone, tablet } = isMobile();
  const forceFullScreen = isBelowMd1 || phone || tablet;

  const onFullScreenToggle = () =>
    setModalMode((prevState) =>
      prevState === ModalMode.Default
        ? ModalMode.FullScreen
        : ModalMode.Default,
    );
  const isFullScreen = forceFullScreen || modalMode === ModalMode.FullScreen;
  const mode = isFullScreen ? ModalMode.FullScreen : ModalMode.Default;

  // Mirrors VideoViewer's accrued playback seconds. Kept here rather than read
  // off VideoViewer at report time because the modal destroys its content on
  // close (`destroyOnHidden`), so the component is already unmounted by the
  // time the closing document's duration is needed.
  const watchedSecondsRef = useRef(0);
  const handleWatchedSecondsChange = useCallback((seconds: number) => {
    watchedSecondsRef.current = seconds;
  }, []);
  const getWatchedSeconds = useCallback(() => watchedSecondsRef.current, []);

  // Anything else renders no viewer at all (see the early return below), so
  // there is no view to record.
  const isViewerRenderable = !!(isPdf || isImage || isVideo);

  const { confirmRead } = useDocumentationViewTracking({
    documentationVersionId:
      isOpen && isViewerRenderable ? documentationVersionId : null,
    // Video reports real playback time; PDFs and images use wall-clock time
    // the viewer stayed open.
    getWatchedSeconds: isVideo ? getWatchedSeconds : undefined,
  });

  // Clear the playback counter for the next viewing session. Keyed on `isOpen`
  // as well as the attachment, because surfaces that keep the attachment
  // mounted across open/close (DocumentViewDetails, InlineDocChip) would
  // otherwise carry the previous session's seconds into the next one, and a
  // freshly mounted VideoViewer only reports once playback passes a whole
  // second. This must be an effect declared *after* the tracking hook, not a
  // render-phase reset: React runs every cleanup before any setup, so the
  // closing document's duration is banked first and only then is the counter
  // zeroed.
  useEffect(() => {
    watchedSecondsRef.current = 0;
  }, [attachment?.id, isOpen]);

  // Video is "processing" when BE has not yet marked the attachment processed
  // and no transcoded variant has appeared yet.
  // `original` is included because the placeholder is about playability —
  // if BE populated `original` before transcoding finished, the user can
  // still play the raw upload.
  const hasPlayableUrl =
    !!attachment?.urls?.video1080p ||
    !!attachment?.urls?.video720p ||
    !!attachment?.urls?.video480p ||
    !!attachment?.urls?.original;
  const isProcessingVideo =
    !!isVideo && attachment?.processed === false && !hasPlayableUrl;

  const { timedOut: processingTimedOut } = useProcessingPolling({
    enabled: !!(isOpen && isProcessingVideo && onRefetchAttachment),
    onPoll: () => onRefetchAttachment?.(),
    intervalMs: VIDEO_PROCESSING_POLL_INTERVAL_MS,
    timeoutMs: VIDEO_PROCESSING_POLL_TIMEOUT_MS,
  });

  const [isConfirmingRead, setIsConfirmingRead] = useState(false);

  const triggerMarkAsRead = useCallback(async () => {
    if (!onMarkAsRead) return;
    setIsConfirmingRead(true);
    try {
      const result = await confirmRead();
      if (!result) {
        toast({ content: t('something-went-wrong') });
        return;
      }
      toast({
        content: isVideo ? t('marked-as-watched') : t('marked-as-read'),
      });
      onMarkAsRead(result.readingDurationSeconds, result.id);
    } catch (error) {
      showGraphQLErrors(error as ErrorLike, t);
    } finally {
      setIsConfirmingRead(false);
    }
  }, [onMarkAsRead, confirmRead, t, isVideo]);

  const ctaLabel = isVideo
    ? t('i-have-watched-this-video')
    : t('i-have-read-this-document');
  const ctaTestId = isVideo
    ? 'file-viewer-modal__mark-as-watched'
    : 'file-viewer-modal__mark-as-read';

  const fixedActions =
    useMarkAsReadTool &&
    onMarkAsRead &&
    !isProcessingVideo &&
    !isMarkedAsRead ? (
      <Button
        buttonText={ctaLabel}
        buttonType={ButtonTypes.primaryFilled}
        buttonSize={ButtonSizes.large}
        rightIcon="check"
        fullWidth
        isLoading={isConfirmingRead}
        onClick={triggerMarkAsRead}
        data-test-id={ctaTestId}
      />
    ) : undefined;

  if (!isViewerRenderable) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      caption={attachmentName}
      modalContentClassName={styles.modalContentClassName}
      modalBodyClassName={styles.modalBodyClassName}
      mode={mode}
      fixedActions={fixedActions}
    >
      <>
        {isPdf && (
          <PDFViewer
            fileUrl={fileUrl}
            downloadUrl={downloadUrl}
            onFullScreenToggle={
              forceFullScreen ? undefined : onFullScreenToggle
            }
            isFullScreen={isFullScreen}
            useDownloadTool={useDownloadTool}
            useFavoriteTool={useFavoriteTool}
            onFavoriteToggle={onFavoriteToggle}
            isFavorited={isFavorited}
            isFavoriteLoading={isFavoriteLoading}
          />
        )}
        {isImage && (
          <ImageViewer
            fileUrl={fileUrl}
            downloadUrl={downloadUrl}
            onFullScreenToggle={
              forceFullScreen ? undefined : onFullScreenToggle
            }
            swiperProps={swiperProps}
            useDownloadTool={useDownloadTool}
            useFavoriteTool={useFavoriteTool}
            onFavoriteToggle={onFavoriteToggle}
            isFavorited={isFavorited}
            isFavoriteLoading={isFavoriteLoading}
          />
        )}
        {isVideo && isProcessingVideo && (
          <div
            className={videoStyles.processingPlaceholder}
            data-test-id="file-viewer-modal__video-processing"
          >
            <CircleLoader />
            <span className={videoStyles.processingPlaceholderCaption}>
              {processingTimedOut
                ? t('still-processing-try-again-later')
                : t('processing-video')}
            </span>
          </div>
        )}
        {isVideo && !isProcessingVideo && (
          <VideoViewer
            fileUrl={fileUrl}
            downloadUrl={downloadUrl}
            posterUrl={attachment?.urls?.imagePreview || undefined}
            videoUrls={{
              video1080p: attachment?.urls?.video1080p,
              video720p: attachment?.urls?.video720p,
              video480p: attachment?.urls?.video480p,
              original: attachment?.urls?.original,
            }}
            onWatchedSecondsChange={handleWatchedSecondsChange}
            onFullScreenToggle={onFullScreenToggle}
            isFullScreen={isFullScreen}
            useDownloadTool={useDownloadTool}
            useFavoriteTool={useFavoriteTool}
            onFavoriteToggle={onFavoriteToggle}
            isFavorited={isFavorited}
            isFavoriteLoading={isFavoriteLoading}
          />
        )}
      </>
    </Modal>
  );
}
